import { z } from 'zod';

/** Краткая карточка пригласившего — только для показа источника в реестре. */
export const AdminUserReferrerDto = z.object({
  id: z.uuid(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
});
export type AdminUserReferrerDto = z.infer<typeof AdminUserReferrerDto>;

export const AdminUserDto = z.object({
  id: z.uuid(),
  telegramId: z.string().nullable(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  isPremium: z.boolean(),
  /** Фейк-аккаунт «массовки» (networking); в реестре скрыт по умолчанию. */
  isFake: z.boolean(),
  cityId: z.uuid().nullable(),
  /**
   * Денормализованный баланс энергии (⚡); истина — леджер CoinTransaction.
   * Без `nonnegative()`: ручная корректировка (`delta` < 0 в `/admin/coins/adjust`)
   * не отсекается по нижней границе, баланс может уйти в минус — как и
   * `CoinTransactionDto.balanceAfter`.
   */
  coinBalance: z.number().int(),
  /** Доступно к трате: coinBalance + сумма отрицательных PENDING. */
  energyAvailable: z.number().int(),
  adminRoles: z.array(z.enum(['SUPERADMIN', 'CITY_MODERATOR', 'CRM_MANAGER'])),
  crmManagerPartnerSharePercent: z.number().int().min(0).max(100).nullable(),
  /** Города роли CITY_MODERATOR (UserAdminCityScope, #166); пусто без роли. */
  moderatedCityIds: z.array(z.uuid()),
  // Подписка на дайджест очереди импорта. Уведомления получают админы с этим флагом:
  // SUPERADMIN — по всем городам, CITY_MODERATOR — по своему (см. channel-import-digest.handler).
  notifyChannelImport: z.boolean(),
  // Подписка на уведомления о новом фото знакомств, ждущем модерации
  // (см. photo-moderation-new.handler). По умолчанию выключена.
  notifyPhotoModeration: z.boolean(),
  // Подписка на уведомления о новых задачах продвижения событий
  // (см. promotion-task-new.handler). По умолчанию включена.
  notifyPromotionTasks: z.boolean(),
  // Подписка на алерты о деградации каналов-источников
  // (см. channel-health-alert.handler). По умолчанию выключена.
  notifyChannelHealth: z.boolean(),
  // Подписка SUPERADMIN на заявки с витрины пакетов. По умолчанию включена.
  notifyMembershipLeads: z.boolean(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime().nullable(),
  /** Первый `/start` в чате бота; null = гейт Start ещё не пройден. */
  botStartedAt: z.iso.datetime().nullable(),
  /** Момент, когда юзер выгнал бота из чата; null = бот доступен для рассылок. */
  botBlockedAt: z.iso.datetime().nullable(),
  /** Кто пригласил (реферальная ссылка); null = пришёл сам. */
  referredBy: AdminUserReferrerDto.nullable(),
  /**
   * VIP активен на момент чтения: статус выдан и срок не вышел.
   * Считается на сервере (`isVipActive`) — у клиента нет доверенного `now`.
   */
  isVip: z.boolean(),
  /** До когда VIP активен; null = бессрочно ЛИБО статус не выдан (см. isVip). */
  vipUntil: z.iso.datetime().nullable(),
  /** Инвестор выдан. Срока нет: отметка либо есть, либо снята. */
  isInvestor: z.boolean(),
  /*
   * Счётчики карточки считаются по 3 запроса на юзера, поэтому только для одного
   * юзера (`GET /admin/users/:id` и ответы мутаций карточки); в реестре и поиске — null.
   * Комментарии команды о контакте — отдельно, `GET /admin/crm/comments?userId=`.
   */
  /** Прошедшие CONFIRMED EVENT|MEETING — те же правила, что ConnectionCardStatsDto.meetings. */
  meetingsCount: z.number().int().nonnegative().nullable(),
  /** Уникальные связи: ProfileMatch ∪ DIRECT — как ConnectionCardStatsDto.friends. */
  friendsCount: z.number().int().nonnegative().nullable(),
  /** Сколько людей привёл по рефералке — как ConnectionCardStatsDto.referrals. */
  referralsCount: z.number().int().nonnegative().nullable(),
});
export type AdminUserDto = z.infer<typeof AdminUserDto>;

export const AdminUserListResponse = z.object({
  items: z.array(AdminUserDto),
  /** Всего строк под текущими фильтрами (без учёта страницы). */
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponse>;

/** Статус юзера относительно чата бота — производный от botStartedAt/botBlockedAt. */
export const AdminUserBotStatus = z.enum(['started', 'not_started', 'blocked']);
export type AdminUserBotStatus = z.infer<typeof AdminUserBotStatus>;

/** Есть ли пригласивший (пришёл по реферальной ссылке) или нет. */
export const AdminUserReferralFilter = z.enum(['with', 'without']);
export type AdminUserReferralFilter = z.infer<typeof AdminUserReferralFilter>;

export const AdminUserSort = z.enum(['createdAt', 'lastSeenAt']);
export type AdminUserSort = z.infer<typeof AdminUserSort>;

export const ADMIN_USER_LIST_LIMIT_DEFAULT = 25;
export const ADMIN_USER_LIST_LIMIT_MAX = 100;

export const AdminUserListQuery = z.object({
  /** Подстрока: username / firstName / lastName / telegramId. */
  search: z.string().trim().min(1).max(64).optional(),
  cityId: z.uuid().optional(),
  /** Границы включительные, по календарным дням в UTC. */
  createdFrom: z.iso.date().optional(),
  createdTo: z.iso.date().optional(),
  lastSeenFrom: z.iso.date().optional(),
  lastSeenTo: z.iso.date().optional(),
  botStatus: AdminUserBotStatus.optional(),
  referral: AdminUserReferralFilter.optional(),
  /** Кого пригласил конкретный юзер (ссылка «приглашённые» из карточки). */
  referredById: z.uuid().optional(),
  adminRole: z.enum(['SUPERADMIN', 'CITY_MODERATOR', 'CRM_MANAGER']).optional(),
  includeFakes: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .default(false),
  sort: AdminUserSort.default('createdAt'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(ADMIN_USER_LIST_LIMIT_MAX).default(ADMIN_USER_LIST_LIMIT_DEFAULT),
});
export type AdminUserListQuery = z.infer<typeof AdminUserListQuery>;

export const AdminUserSearchQuery = z.object({
  username: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_]+$/, 'telegram username: letters, digits, underscore'),
});
export type AdminUserSearchQuery = z.infer<typeof AdminUserSearchQuery>;

/**
 * Выдача/снятие админ-роли. CITY_MODERATOR выдаётся сразу с городами (≥1);
 * повторная выдача заменяет набор городов. Снятие удаляет все города.
 */
export const AdminUserUpdateRolesBody = z.union([
  z.object({
    role: z.literal('CRM_MANAGER'),
    value: z.literal(true),
    cityIds: z.array(z.uuid()).min(1).max(50).optional(),
  }),
  z.object({ role: z.literal('CRM_MANAGER'), value: z.literal(false) }),
  z.object({ role: z.literal('SUPERADMIN'), value: z.boolean() }),
  z.object({
    role: z.literal('CITY_MODERATOR'),
    value: z.literal(true),
    cityIds: z.array(z.uuid()).min(1).max(50),
  }),
  z.object({ role: z.literal('CITY_MODERATOR'), value: z.literal(false) }),
]);
export type AdminUserUpdateRolesBody = z.infer<typeof AdminUserUpdateRolesBody>;

export const AdminUserCrmManagerPartnerShareBody = z.object({
  percent: z.number().int().min(0).max(100).nullable(),
});
export type AdminUserCrmManagerPartnerShareBody = z.infer<typeof AdminUserCrmManagerPartnerShareBody>;

/** Тело переключения подписки админа на дайджест очереди импорта. */
export const AdminUserNotifyChannelImportBody = z.object({
  value: z.boolean(),
});
export type AdminUserNotifyChannelImportBody = z.infer<typeof AdminUserNotifyChannelImportBody>;

/** Тело переключения подписки админа на уведомления о новых фото на модерацию. */
export const AdminUserNotifyPhotoModerationBody = z.object({
  value: z.boolean(),
});
export type AdminUserNotifyPhotoModerationBody = z.infer<typeof AdminUserNotifyPhotoModerationBody>;

/** Тело переключения подписки админа на уведомления о новых задачах продвижения. */
export const AdminUserNotifyPromotionTasksBody = z.object({
  value: z.boolean(),
});
export type AdminUserNotifyPromotionTasksBody = z.infer<typeof AdminUserNotifyPromotionTasksBody>;

/** Тело переключения подписки админа на алерты о деградации каналов-источников. */
export const AdminUserNotifyChannelHealthBody = z.object({
  value: z.boolean(),
});
export type AdminUserNotifyChannelHealthBody = z.infer<typeof AdminUserNotifyChannelHealthBody>;

/** Тело переключения подписки SUPERADMIN на заявки с витрины пакетов. */
export const AdminUserNotifyMembershipLeadsBody = z.object({
  value: z.boolean(),
});
export type AdminUserNotifyMembershipLeadsBody = z.infer<typeof AdminUserNotifyMembershipLeadsBody>;

/**
 * Тело выдачи/отзыва VIP. `until: null` при `granted: true` = бессрочный статус;
 * при отзыве срок запрещён — снятие статуса не может нести дату, и молчаливо
 * проглоченный `until` выглядел бы как «отозвал до», чего в модели нет.
 */
export const AdminUserVipBody = z
  .object({
    granted: z.boolean(),
    until: z.iso.datetime().nullable().default(null),
  })
  .refine((v) => v.granted || v.until === null, {
    message: 'until is only allowed when granting VIP',
    path: ['until'],
  });
export type AdminUserVipBody = z.infer<typeof AdminUserVipBody>;

/** Тело выдачи/снятия инвестора. Срока нет. */
export const AdminUserInvestorBody = z.object({
  granted: z.boolean(),
});
export type AdminUserInvestorBody = z.infer<typeof AdminUserInvestorBody>;
